import { FilterIcon } from "@heroicons/react/outline";
import { UserAddIcon } from "@heroicons/react/solid";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { classNames } from "../../../helpers";
import CandidateModel from "../../../api/models/CandidateModel";
import { useInfiniteQuery, useQuery } from "react-query";
import { indexCandidates } from "../../../api/endpoints/liaisons/candidates";
import StudentModel from "../../../api/models/StudentModel";
import { indexSponsorships } from "../../../api/endpoints/liaisons/sponsorships";
import { FullSpinner } from "../../../components/LoadingSpinner";
import AddCandidateModal from "../../../components/modal/views/AddCandidateModal";
import SponsorshipModel from "../../../api/models/SponsorshipModel";
import SponsorModel from "../../../api/models/SponsorModel";
import AppFilters from "../../../components/app-filters/AppFilters";
import { SubmissionStatus } from "../../../api/models/CandidateSubmissionModel";
import ShowIf from "../../../components/show-if/ShowIf";
import { useNavigate } from "react-router-dom";
import { indexLiaisonsStudents } from "../../../api/endpoints/liaisons/students";
import CandidateTable from "./CandidateTable";
import { ApiCommonParams } from "../../../api/@types/apiTypes";
import Pagination from "../../../components/pagination/Pagination";

type ICandidateFilters = {
  sponsor?: number;
  sponsorship?: number;
  tutor?: number;
  status?: string;
};

export default function CandidatesView() {
  const LocalBasePath = "page.liaison.dashboard.candidates";
  const { t } = useTranslation("", { keyPrefix: LocalBasePath });

  const [modalInitialData, setModalInitialData] = useState<CandidateModel>();
  const navigate = useNavigate();
  const [filters, setFilters] = useState<ICandidateFilters>({});
  const [currentPage, setCurrentPage] = useState(1);
  const resetCurrentPage = () => {
    setCurrentPage(1);
  };
  const [openForm, setOpenForm] = useState(false);

  const [showFilters, setShowFilters] = useState(false);
  let [currentPageFilter, setCurrentPageFilter] = useState<{
    [key: string]: any;
  }>({
    currentSponsorshipPage: 1,
  });

  const [action, setAction] = useState<any>("add");
  const {
    data: candidates,
    refetch: refetchCandidates,
    isLoading,
  } = useQuery(
    ["candidates", filters, currentPage],
    () => {
      let apiParams: ApiCommonParams = {
        filters,
        orderBy: "-id",
        pagination: {
          pageSize: 10,
          pageNumber: currentPage,
        },
      };
      return indexCandidates(apiParams);
    },
    {
      select: (apiResponse) => {
        if (apiResponse.isSuccess) {
          const data: CandidateModel[] = CandidateModel.fromApiResponse(
            apiResponse.data
          );
          return { data, pagination: apiResponse.pagination };
        }
      },
    }
  );

  const { data: students } = useQuery(
    ["unscheduledLiaisonStudentsExtended", currentPage],
    () => {
      let apiParams: ApiCommonParams = {
        viewName: "extended",
        filters: { is_scheduled: false },
        pagination: {
          pageSize: 5,
          pageNumber: currentPage,
        },
      };
      return indexLiaisonsStudents(apiParams);
    },
    {
      staleTime: 0,
      select: (response) => {
        if (response.isSuccess) {
          const data: StudentModel[] = StudentModel.fromApiResponse(
            response.data
          );
          return { data, pagination: response.pagination };
        }
      },
    }
  );

  useEffect(() => {
    resetCurrentPage();
  }, [filters]);

  const { data: sponsorships, fetchNextPage: fetchNextPageSponsorship } =
    useInfiniteQuery(
      ["sponsorship"],

      ({ pageParam = 1 }) => {
        return indexSponsorships({
          pagination: {
            pageSize: 25,
            pageNumber: pageParam,
          },
        });
      },

      {
        select: (data) => {
          let sponsorshipsList: SponsorshipModel[] = [];

          return {
            pages: data.pages
              .map((pageResponse) => {
                if (pageResponse.isSuccess) {
                  sponsorshipsList = SponsorshipModel.fromApiResponse(
                    pageResponse.data
                  );
                }
                return sponsorshipsList;
              })
              .flat(),
            pageParams: data.pageParams,
          };
        },
        cacheTime: 0,
      }
    );

  useEffect(() => {
    fetchNextPageSponsorship({
      pageParam: currentPageFilter.currentSponsorshipPage,
    });
  }, [currentPageFilter.currentSponsorshipPage]);
  const openCandidateModal = (candidate?: CandidateModel) => {
    setModalInitialData(candidate);
    if (!!candidate) {
      setAction("view");
    } else {
      setAction("add");
    }
    setOpenForm(true);
  };

  const onScheduleStudent = (student: StudentModel) => {
    navigate(`/liaison/dashboard/schedule?student_id=${student.id}`);
  };

  const statusOptions: {
    key: string;
    value: string;
  }[] = Object.values(SubmissionStatus).map((value) => ({
    key: t(value),
    value,
  }));

  const sponsorshipOptions =
    sponsorships?.pages.map((sponsorship: SponsorshipModel) => ({
      key: `${sponsorship.sponsor?.name} \\ ${sponsorship.id}`,
      value: `${sponsorship.id}`,
    })) || [];

  const getSponsors = (sponsorships: SponsorshipModel[]) => {
    let sponsors: SponsorModel[] = [];
    sponsorships.reduce(
      (frequency: { [key: string]: boolean }, sponsorship) => {
        if (!(sponsorship?.sponsor?.id! in frequency)) {
          sponsors.push(sponsorship?.sponsor!);
          frequency[sponsorship?.sponsor?.id!] = true;
        }
        return frequency;
      },
      {}
    );
    return sponsors;
  };

  const sponsorsOptions = getSponsors(sponsorships?.pages || []).map(
    (sponsor) => ({
      key: `${sponsor?.name}`,
      value: `${sponsor?.id}`,
    })
  );
  const onCandidateClick = (candidate: CandidateModel) => {
    openCandidateModal(candidate);
  };

  const onCancelFilters = () => {
    setShowFilters(false);
    setFilters({});
  };

  const hasCandidates =
    (!!candidates?.data && !!candidates.data.length) ||
    (!!students?.data && !!students.data.length);
  return (
    <div className="w-full flex flex-col">
      <div className="border-b">
        <div className="bg-gray-50 p-4 divide-gray-300 text-gray-900 flex-1 flex flex-col sm:flex-row gap-4 sm:items-center justify-between">
          <h2 className="text-2xl font-semibold">{t("candidateRequests")}</h2>
          <div className="flex gap-2">
            <button
              type="button"
              data-cy="candidateFilters"
              onClick={() => setShowFilters((x) => !x)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandblue-500"
            >
              <div className="flex items-center justify-start gap-2">
                <FilterIcon
                  className="h-5 w-5 text-gray-500"
                  aria-hidden="true"
                />
                {t("filter")}
              </div>
            </button>
            <button
              type="button"
              data-cy="addCandidate"
              onClick={() => openCandidateModal()}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-brandblue-600 hover:bg-brandblue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandblue-500"
            >
              <div className="flex items-center justify-start gap-2">
                <UserAddIcon className="h-5 w-5" aria-hidden="true" />
                {t("addCandidate")}
              </div>
            </button>
          </div>
        </div>
        <div className={classNames("rounded-md shadow-sm bg-gray-50 pb-6")}>
          {/* <div className="relative mx-4">
            <div className="absolute inset-y-0 start-0 ps-3  flex items-center pointer-events-none">
              <SearchIcon
                className="h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            </div>
            <input
              type="text"
              name="serarch"
              id="serarch"
              className="focus:ring-brandblue-500 focus:border-brandblue-500 block w-full ps-10  sm:text-sm border-gray-300 rounded-md"
              placeholder={t("search")}
            />
          </div> */}
          <ShowIf condition={showFilters}>
            <AppFilters
              className="pt-1 mx-4 flex flex-col md:flex-row gap-4"
              filters={filters}
              setFilters={setFilters}
              onLoadMore={(key: string) =>
                setCurrentPageFilter({
                  ...currentPageFilter,
                  [key]: ++currentPageFilter[key],
                })
              }
              fields={[
                {
                  name: "by_candidate_submission_status",
                  label: t("status"),
                  options: statusOptions,
                },
                {
                  name: "by_sponsor_id",
                  label: t("sponsor"),
                  options: sponsorsOptions,
                },
                {
                  name: "sponsorship_id",
                  label: t("sponsorship"),
                  options: sponsorshipOptions,
                  paginationLabel: "currentSponsorshipPage",
                },
              ]}
              handleButtonClick={onCancelFilters}
            />
          </ShowIf>
        </div>
      </div>
      <ShowIf condition={isLoading}>
        <FullSpinner size={40} />
      </ShowIf>
      <ShowIf condition={!isLoading && hasCandidates}>
        <div className="h-full flex flex-col">
          <CandidateTable
            candidates={candidates?.data || []}
            students={students?.data || []}
            onScheduleStudent={onScheduleStudent}
            onCandidateClick={onCandidateClick}
          />

          <div className="sticky mt-auto bottom-0">
            <Pagination
              hasNext={
                !!candidates?.pagination?.hasNext ||
                !!students?.pagination?.hasNext
              }
              hasPrev={
                !!candidates?.pagination?.hasPrev ||
                !!students?.pagination?.hasPrev
              }
              goToPage={(page: number) => setCurrentPage(page)}
              currentPage={currentPage}
              numberOfPages={Math.max(
                Number(candidates?.pagination?.pagesCount || 0),
                Number(students?.pagination?.pagesCount || 0)
              )}
            />
          </div>
        </div>
      </ShowIf>
      <ShowIf condition={!isLoading && !hasCandidates}>
        <div className="h-full flex flex-col  items-center pt-16" data-cy="emptyCandidateWrapper">
          <h2 className="text-lg font-medium text-gray-700">
            {t("noCandidates")}
          </h2>
          <div className="text-base font-normal text-gray-400 mt-1">
            {t("startAdding")}
          </div>
          <button
            type="button"
            onClick={() => openCandidateModal()}
            className="inline-flex items-center gap-3 mt-6 px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-brandblue-600 hover:bg-brandblue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandblue-500"
          >
            <UserAddIcon className="h-5 w-5" aria-hidden="true" />
            {t("addCandidate")}
          </button>
        </div>
      </ShowIf>
      <AddCandidateModal
        initialData={modalInitialData}
        action={action}
        setActionStatus={setAction}
        isOpen={openForm}
        setIsOpen={setOpenForm}
        onSave={refetchCandidates}
        sponsorshipOptions={sponsorshipOptions}
      />
    </div>
  );
}
